/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: CdrDataGrid.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Data grid específico para Call Detail Records con filtros integrados
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { PhoneCall } from 'lucide-react';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import DateRangePicker, { DateRange } from '../../../components/ui/DateRangePicker';

import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { CdrFilterValues } from '../types';
import { useSavedFilters } from '../hooks/useSavedFilters';

interface CdrDataGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: any[];
  loading: boolean;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  searchQuery: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filters: CdrFilterValues;
  onSearch: (query: string) => void;
  onSort: (column: string) => void;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (limit: number) => void;
  onColumnToggle: (key: string) => void;
  onFilterChange: (filters: CdrFilterValues) => void;
}

export function CdrDataGrid({
  data,
  columns,
  loading,
  pagination,
  searchQuery,
  sortBy,
  sortOrder,
  filters,
  onSearch,
  onSort,
  onPageChange,
  onItemsPerPageChange,
  onColumnToggle,
  onFilterChange,
}: CdrDataGridProps) {
  const { t } = useI18n();
  const { savedFilters, loading: loadingFilters } = useSavedFilters();
  const [selectedFilterId, setSelectedFilterId] = React.useState<string>('');

  // Handle saved filter selection
  const handleSavedFilterChange = (filterId: string) => {
    if (!filterId) {
      setSelectedFilterId('');
      return;
    }
    
    setSelectedFilterId(filterId);
    
    const selectedFilter = savedFilters.find(f => f.id.toString() === filterId);
    if (!selectedFilter) return;

    // Convert CDR Filter format to simple filter values
    const newFilters: CdrFilterValues = {
      startDate: filters.startDate, // Keep existing dates
      endDate: filters.endDate,
      caller: '',
      destination: '',
      direction: '',
      hangupCause: '',
      savedFilterId: selectedFilter.id,
    };

    // Extract values from search conditions
    selectedFilter.filterData.searchConditions.forEach(condition => {
      if (condition.exclude) return; // Skip excluded conditions for now
      
      switch (condition.searchBy) {
        case 'caller_id':
        case 'source':
          newFilters.caller = condition.value;
          break;
        case 'destination':
        case 'did':
          newFilters.destination = condition.value;
          break;
        case 'call_type':
          // Convert "local" to "internal" for consistency
          newFilters.direction = condition.value === 'local' ? 'internal' : condition.value;
          break;
        case 'hangup_cause':
          newFilters.hangupCause = condition.value;
          break;
      }
    });

    // Apply the converted filters
    onFilterChange(newFilters);
  };

  // Wrapper for manual filter changes (clears saved filter selection)
  const handleManualFilterChange = (newFilters: CdrFilterValues) => {
    setSelectedFilterId(''); // Clear saved filter selection
    onFilterChange(newFilters);
  };

  return (
    <div className="cdr-wrapper">
      {/* Filters Card - PRIMERO */}
      <div className="pt-16">
        <div className="p-3 bg-card/75 backdrop-blur-md border border-border/50 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Saved Filters */}
            <SimpleTooltip
              content={t('callDetailRecords.filters.savedFilters.tooltip')}
              title={t('callDetailRecords.filters.savedFilters.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('callDetailRecords.filters.savedFilters.label')}</Label>
            </SimpleTooltip>
            <select
              className="rounded-md border border-border bg-background px-3 py-1.5 text-sm h-9"
              value={selectedFilterId}
              onChange={(e) => { handleSavedFilterChange(e.target.value); }}
              disabled={loadingFilters || savedFilters.length === 0}
            >
              <option value="">
                {loadingFilters
                  ? 'Loading...'
                  : savedFilters.length === 0
                    ? t('callDetailRecords.filters.savedFilters.empty') || 'No saved filters'
                    : 'Select a filter...'}
              </option>
              {savedFilters.map(filter => (
                <option key={filter.id} value={filter.id}>{filter.description}</option>
              ))}
            </select>

            {/* Date Range Picker */}
            <Label className="text-left">
              {t('callDetailRecords.filters.dateRange') || 'Date Range'}
            </Label>
            <DateRangePicker
              value={{
                startDate: filters.startDate ? filters.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
                endDate:   filters.endDate   ? filters.endDate.split('T')[0]   : new Date().toISOString().split('T')[0],
                startTime: filters.startDate && filters.startDate.includes('T') ? filters.startDate.split('T')[1].slice(0, 5) : '00:00',
                endTime:   filters.endDate   && filters.endDate.includes('T')   ? filters.endDate.split('T')[1].slice(0, 5)   : '23:59',
              }}
              onChange={(range: DateRange) => {
                handleManualFilterChange({
                  ...filters,
                  startDate: `${range.startDate}T${range.startTime || '00:00'}`,
                  endDate:   `${range.endDate}T${range.endTime   || '23:59'}`,
                });
              }}
            />


            {/* Línea 3: Caller y Destination */}
            <SimpleTooltip
              content={t('callDetailRecords.filters.caller.tooltip')}
              title={t('callDetailRecords.filters.caller.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('callDetailRecords.filters.caller.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.caller}
              onChange={(e) => handleManualFilterChange({ ...filters, caller: e.target.value })}
              placeholder={t('callDetailRecords.filters.caller.placeholder')}
              className="h-9"
            />
            <SimpleTooltip
              content={t('callDetailRecords.filters.destination.tooltip')}
              title={t('callDetailRecords.filters.destination.label')}
              position="top"
              maxWidth="300px"
            >
              <Label className="text-left">{t('callDetailRecords.filters.destination.label')}</Label>
            </SimpleTooltip>
            <Input
              type="text"
              value={filters.destination}
              onChange={(e) => handleManualFilterChange({ ...filters, destination: e.target.value })}
              placeholder={t('callDetailRecords.filters.destination.placeholder')}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Data Grid - DESPUÉS */}
      <DataGridSimple
        data={data}
        columns={columns}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={onSearch}
        onSort={onSort}
        onPageChange={onPageChange}
        onItemsPerPageChange={onItemsPerPageChange}
        onColumnToggle={onColumnToggle}
        title={t('callDetailRecords.title')}
        titleIcon={<PhoneCall className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('callDetailRecords.searchPlaceholder')}
        emptyMessage={t('callDetailRecords.emptyMessage')}
      />
      
      <style dangerouslySetInnerHTML={{__html: `
        .cdr-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0;
        }
        
        .cdr-wrapper .space-y-6 > :not([hidden]) ~ :not([hidden]) {
          margin-top: 0 !important;
        }
      `}} />
    </div>
  );
}
